import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AdminOrgRow, AdminOrgDetail, AdminPaged } from '@repo/shared-types';
import type { AssignPlanInput } from '@repo/validation';
import { adminFetchApi } from '../lib/api';

export interface AdminListParams {
  page: number;
  limit: number;
  sort: string;
  q?: string;
}

export function useAdminOrgs(p: AdminListParams) {
  const qs = new URLSearchParams({ page: String(p.page), limit: String(p.limit), sort: p.sort });
  if (p.q) qs.set('q', p.q);
  return useQuery({
    queryKey: ['admin', 'orgs', p],
    queryFn: () => adminFetchApi<AdminPaged<AdminOrgRow>>(`/admin/orgs?${qs}`),
  });
}

export function useAdminOrg(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'orgs', id],
    queryFn: () => adminFetchApi<AdminOrgDetail>(`/admin/orgs/${id}`),
    enabled: !!id,
  });
}

export function useAssignPlan(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignPlanInput) =>
      adminFetchApi(`/admin/orgs/${orgId}/plan`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      toast.success('Đã gán gói');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useSetOrgSuspended(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (suspend: boolean) =>
      adminFetchApi(`/admin/orgs/${orgId}/${suspend ? 'suspend' : 'unsuspend'}`, { method: 'POST' }),
    onSuccess: (_d, suspend) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      toast.success(suspend ? 'Đã khoá tổ chức' : 'Đã mở khoá tổ chức');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
